import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { media } from '@atlaskit/primitives';

import { Adg4Button } from '@/components/adg4/button/src/button';
import TryIntent from '@/components/try-intent/src/try-intent';
import { Picture } from '@/components/wpl/image/src/image';
import { wacToken } from '@/libraries/design/foundations';
import { useLocalizedHref } from '@/libraries/web-framework/src/localization/use-localized-href';
import { checkFeatureFlagGate } from '@/utils/feature-flags/check-feature-flag';
import { featureFlags } from '@/utils/feature-flags/feature-flag-manifest';

export const ClosingBanner = () => {
	const { formatMessage } = useIntl();

	const useGWP7617Gate = checkFeatureFlagGate(featureFlags.gwp7617JiraShortTermAiInfusionGate);

	const text = useGWP7617Gate
		? formatMessage({
				id: 'jira-product-tour.closing-banner.header-text-gwp-7617',
				defaultMessage: 'No matter what you dream up, Jira helps you get it done.',
			})
		: formatMessage({
				id: 'jira-product-tour.closing-banner.header-text',
				defaultMessage: "No matter what you're trying to dream up, Jira helps you get it done",
			});

	const buttonText = formatMessage({
		id: 'jira-product-tour.closing-banner.button-text',
		defaultMessage: 'Get Jira free',
	});

	const ctaButtonUrl = '/try/cloud/signup?bundle=jira-software';

	return (
		<div css={ctaBannerContainer}>
			<div css={contentContainer}>
				<h2 css={headerStyles}>{text}</h2>
				<TryIntent
					intentType={'button'}
					containerId={'jira-product-tour-overview-closing-banner'}
					productKey={'jira-software.ondemand'}
				>
					<Adg4Button
						variant="primary"
						size="small"
						color="yellow"
						href={useLocalizedHref(ctaButtonUrl)}
						dataEventProps={{
							'data-product-key': 'jira',
							'data-event': 'clicked',
							'data-event-component': 'linkButton',
							'data-event-container': 'ctaSection',
							'data-label': 'get-jira-free',
							'data-label-english': `${buttonText}`,
							'data-track-event': 'jira-pt.closing-banner.get-jira-free',
						}}
					>
						{buttonText}
					</Adg4Button>
				</TryIntent>
			</div>
			<div css={imageContainer}>
				<div css={imageStyles}>
					<Picture
						image={{
							src: 'https://dam-cdn.atl.orangelogic.com/AssetLink/865y3n8f2i1843j2vs45535l48vtx77r.webp',
							alt: '',
							width: 303,
							height: 303,
						}}
					/>
				</div>
				<div css={overlayStarImage}>
					<Picture
						image={{
							src: 'https://dam-cdn.atl.orangelogic.com/AssetLink/xbvh6b7g0mjibasf143rj6711054m0cf.webp',
							alt: '',
							width: 95,
							height: 97,
						}}
					/>
				</div>
			</div>
		</div>
	);
};

ClosingBanner.displayName = 'ClosingBanner';

const ctaBannerContainer = css({
	display: 'flex',
	justifyContent: 'space-around',
	alignItems: 'center',
	maxWidth: '1550px',
	margin: '0 auto',
	[media.below.lg]: {
		padding: '0 10%',
	},
	[media.below.sm]: {
		flexDirection: 'column',
		justifyContent: 'center',
	},
});

const contentContainer = css({
	display: 'grid',
	padding: '0 20px',
	gap: '40px',
	[media.below.md]: {
		paddingBlockEnd: '60px',
	},
	[media.below.sm]: {
		justifyItems: 'center',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['a']: {
		borderRadius: '4px',
	},
});

const headerStyles = css({
	maxWidth: '765px',
	fontFamily: wacToken('font.charlie.display'),
	fontWeight: '700',
	fontSize: '40px',
	lineHeight: '48px',
	margin: 0,
	color: wacToken('color.neutral.light0'),
	[media.below.sm]: {
		textAlign: 'center',
		fontSize: '30px',
		lineHeight: '36px',
	},
});

const imageContainer = css({
	position: 'relative',
	width: '303px',
	height: '302px',
	alignSelf: 'end',
	[media.below.lg]: {
		justifySelf: 'center',
	},
	[media.below.sm]: {
		alignSelf: 'center',
	},
});

const imageStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	img: {
		width: '303px',
		height: '302px',
	},
});

const overlayStarImage = css({
	position: 'absolute',
	insetBlockStart: '11px',
	insetInlineStart: '60px',
});
